import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { WorkforceComment } from "@/types/workforce";
import { useAuth } from "@/providers/AuthProvider";

export const useWorkforceComments = (id: string | undefined): {
	comments: WorkforceComment[];
	handleAddComment: (content: string) => Promise<void>;
	handleDeleteComment: (commentId: string) => Promise<void>;
	handleUpdateComment: (commentId: string, content: string) => Promise<void>;
} => {
	const [comments, setComments] = useState<WorkforceComment[]>([]);
	const { user } = useAuth();

	const fetchComments = async () => {
		if (!id) return;

		try {
			const { data: commentsData, error: commentsError } = await supabase
				.from('workforce_comments')
				.select(`
					*,
					profiles:created_by_uuid (
						first_name,
						last_name
					)
				`)
				.eq('workforce_id', id)
				.order('created_at', { ascending: false });

			if (commentsError) {
				console.error("Error fetching comments:", commentsError);
			} else {
				setComments(commentsData as WorkforceComment[]);
			}
		} catch (error) {
			console.error("Error fetching comments:", error);
		}
	};

	useEffect(() => {
		fetchComments();
	}, [id]);

	const handleAddComment = async (content: string) => {
		if (!id || !content.trim() || !user?.id) return;

		try {
			const { data, error } = await supabase
				.from('workforce_comments')
				.insert({
					workforce_id: id,
					content,
					created_by: "Current User",
					created_by_uuid: user.id,
				})
				.select(`
					*,
					profiles:created_by_uuid (
						first_name,
						last_name
					)
				`);

			if (error) throw error;

			toast({
				title: "Comment added",
				description: "Your comment has been added successfully",
			});

			if (data) {
				setComments(prev => [data[0] as WorkforceComment, ...prev]);
			}
		} catch (error) {
			console.error("Error adding comment:", error);
			toast({
				title: "Error adding comment",
				description: "Could not add your comment",
				variant: "destructive",
			});
		}
	};

	const handleDeleteComment = async (commentId: string) => {
		try {
			const { error } = await supabase
				.from('workforce_comments')
				.delete()
				.eq('id', commentId);

			if (error) throw error;

			toast({
				title: "Comment deleted",
				description: "The comment has been deleted successfully",
			});

			setComments(prev => prev.filter(comment => comment.id !== commentId));
		} catch (error) {
			console.error("Error deleting comment:", error);
			toast({
				title: "Error deleting comment",
				description: "Could not delete the comment",
				variant: "destructive",
			});
		}
	};

	const handleUpdateComment = async (commentId: string, content: string) => {
		if (!content.trim()) return;

		try {
			const { error } = await supabase
				.from('workforce_comments')
				.update({
					content,
					updated_at: new Date().toISOString()
				})
				.eq('id', commentId);

			if (error) throw error;

			toast({
				title: "Comment updated",
				description: "The comment has been updated successfully",
			});

			await fetchComments();
		} catch (error) {
			console.error("Error updating comment:", error);
			toast({
				title: "Error updating comment",
				description: "Could not update the comment",
				variant: "destructive",
			});
		}
	};

	return {
		comments,
		handleAddComment,
		handleDeleteComment,
		handleUpdateComment
	};
};
